import { weekFileName } from '../lib/weekFileName';
import { DocLangToggle } from './ui/doc-lang-toggle';
import { useTranslation } from 'react-i18next';
import { WeekRangeFilter } from './ui/week-filter';
import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Download, Filter, Users, Building2, Calendar, FileSpreadsheet, CalendarRange, User, UsersRound, Mail } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TimesheetPrintDialog = ({ 
  open, 
  onOpenChange, 
  timesheet, 
  employees, 
  projects,
  customers,
  weekRange 
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('single');
  
  // Single week filters
  const timesheetEmployeeIds = [...new Set(timesheet?.entries?.map(e => e.employee_id) || [])];
  const timesheetProjectIds = [...new Set(timesheet?.entries?.map(e => e.project_id) || [])];
  const relevantEmployees = employees.filter(emp => timesheetEmployeeIds.includes(emp.id));
  const relevantProjects = projects.filter(proj => timesheetProjectIds.includes(proj.id));

  const [filterOptions, setFilterOptions] = useState({
    allEmployees: true,
    selectedEmployees: [],
    allProjects: true,
    selectedProjects: [],
    allDays: true,
    selectedDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
  });
  const [notes, setNotes] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  // Date range state
  const [rangeStartDate, setRangeStartDate] = useState('');
  const [rangeEndDate, setRangeEndDate] = useState('');
  const [rangeFilterOptions, setRangeFilterOptions] = useState({
    allEmployees: true,
    selectedEmployees: [],
    allProjects: true,
    selectedProjects: [],
    allDays: true,
    selectedDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
  });

  // Employee report state
  const [empReportEmployee, setEmpReportEmployee] = useState('');
  const [empStartDate, setEmpStartDate] = useState('');
  const [empEndDate, setEmpEndDate] = useState('');

  // Batch (multi-employee) report state — generates one PDF/Excel per
  // selected employee for the same date window, then bundles into a ZIP.
  const [batchStartDate, setBatchStartDate] = useState('');
  const [batchEndDate, setBatchEndDate] = useState('');
  const [batchAllEmployees, setBatchAllEmployees] = useState(true);
  const [batchSelectedIds, setBatchSelectedIds] = useState([]);

  useEffect(() => {
    if (open) {
      setFilterOptions(prev => ({
        ...prev,
        selectedEmployees: relevantEmployees.map(e => e.id),
        selectedProjects: relevantProjects.map(p => p.id)
      }));
      setRangeFilterOptions(prev => ({
        ...prev,
        selectedEmployees: employees.map(e => e.id),
        selectedProjects: projects.map(p => p.id)
      }));
      setBatchSelectedIds(employees.map(e => e.id));
    }
  }, [open]);

  const getCustomerNames = () => {
    const selectedProjectIds = filterOptions.allProjects 
      ? relevantProjects.map(p => p.id) 
      : filterOptions.selectedProjects;
    const customerIds = [...new Set(
      projects.filter(p => selectedProjectIds.includes(p.id)).map(p => p.customer_id).filter(Boolean)
    )];
    return [...new Set(customerIds.map(cid => customers?.find(c => c.id === cid)?.name).filter(Boolean))];
  };

  const daysOfWeek = [
    { id: 'monday', label: t('auto.lbl.monday') }, { id: 'tuesday', label: t('auto.lbl.tuesday') },
    { id: 'wednesday', label: t('auto.lbl.wednesday') }, { id: 'thursday', label: t('auto.lbl.thursday') },
    { id: 'friday', label: t('auto.lbl.friday') }, { id: 'saturday', label: t('auto.lbl.saturday') },
    { id: 'sunday', label: t('auto.lbl.sunday') }
  ];

  const handleEmployeeToggle = (employeeId) => {
    setFilterOptions(prev => {
      const newSelected = prev.selectedEmployees.includes(employeeId)
        ? prev.selectedEmployees.filter(id => id !== employeeId)
        : [...prev.selectedEmployees, employeeId];
      return { ...prev, selectedEmployees: newSelected, allEmployees: newSelected.length === relevantEmployees.length };
    });
  };

  const handleAllEmployeesToggle = (checked) => {
    setFilterOptions(prev => ({
      ...prev, allEmployees: checked,
      selectedEmployees: checked ? relevantEmployees.map(emp => emp.id) : []
    }));
  };

  const handleProjectToggle = (projectId) => {
    setFilterOptions(prev => {
      const newSelected = prev.selectedProjects.includes(projectId)
        ? prev.selectedProjects.filter(id => id !== projectId)
        : [...prev.selectedProjects, projectId];
      return { ...prev, selectedProjects: newSelected, allProjects: newSelected.length === relevantProjects.length };
    });
  };

  const handleAllProjectsToggle = (checked) => {
    setFilterOptions(prev => ({
      ...prev, allProjects: checked,
      selectedProjects: checked ? relevantProjects.map(proj => proj.id) : []
    }));
  };

  const handleDayToggle = (dayId) => {
    setFilterOptions(prev => {
      const newSelected = prev.selectedDays.includes(dayId)
        ? prev.selectedDays.filter(id => id !== dayId)
        : [...prev.selectedDays, dayId];
      return { ...prev, selectedDays: newSelected, allDays: newSelected.length === daysOfWeek.length };
    });
  };

  const handleAllDaysToggle = (checked) => {
    setFilterOptions(prev => ({
      ...prev, allDays: checked,
      selectedDays: checked ? daysOfWeek.map(d => d.id) : []
    }));
  };

  // ---- Single Week PDF/Excel ----
  const buildSingleFilters = () => {
    const filters = {};
    if (!filterOptions.allEmployees && filterOptions.selectedEmployees.length > 0) filters.employee_ids = filterOptions.selectedEmployees;
    if (!filterOptions.allProjects && filterOptions.selectedProjects.length > 0) filters.project_ids = filterOptions.selectedProjects;
    if (!filterOptions.allDays && filterOptions.selectedDays.length > 0) filters.days = filterOptions.selectedDays;
    if (notes.trim()) filters.notes = notes.trim();
    const cn = getCustomerNames();
    if (cn.length > 0) filters.customer_names = cn;
    return filters;
  };

  const downloadBlob = (data, filename) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', weekFileName(filename));
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const generateSinglePDF = async () => {
    if (!timesheet?.id) { toast.error(t('auto.proj.noTimesheetAvailable')); return; }
    setDownloading(true);
    try {
      const resp = await axios.post(`${API}/reports/weekly-timesheets/${timesheet.id}/pdf`, buildSingleFilters(), { responseType: 'blob' });
      downloadBlob(resp.data, `timesheet_${timesheet.week_start_date}.pdf`);
      toast.success(t('auto.proj.pdfGenerated'));
      onOpenChange(false);
    } catch (e) { toast.error(t('auto.proj.failedToGeneratePdf')); }
    finally { setDownloading(false); }
  };

  const generateSingleExcel = async () => {
    if (!timesheet?.id) { toast.error(t('auto.proj.noTimesheetAvailable')); return; }
    setDownloadingExcel(true);
    try {
      const resp = await axios.post(`${API}/reports/weekly-timesheets/${timesheet.id}/excel`, buildSingleFilters(), { responseType: 'blob' });
      downloadBlob(resp.data, `timesheet_${timesheet.week_start_date}.xlsx`);
      toast.success(t('auto.proj.excelExported'));
      onOpenChange(false);
    } catch (e) { toast.error(t('auto.proj.failedToExportExcel')); }
    finally { setDownloadingExcel(false); }
  };

  // ---- Multi-Week Range PDF/Excel ----
  const generateRangeReport = async (format) => {
    if (!rangeStartDate || !rangeEndDate) { toast.error(t('auto.proj.selectStartAndEndDates')); return; }
    setDownloading(true);
    try {
      const endpoint = format === 'pdf' ? 'timesheet-range/pdf' : 'timesheet-range/excel';
      const body = { start_date: rangeStartDate, end_date: rangeEndDate };
      if (!rangeFilterOptions.allEmployees && rangeFilterOptions.selectedEmployees.length > 0) body.employee_ids = rangeFilterOptions.selectedEmployees;
      if (!rangeFilterOptions.allProjects && rangeFilterOptions.selectedProjects.length > 0) body.project_ids = rangeFilterOptions.selectedProjects;
      if (!rangeFilterOptions.allDays && rangeFilterOptions.selectedDays.length > 0) body.days = rangeFilterOptions.selectedDays;
      
      const resp = await axios.post(`${API}/reports/${endpoint}`, body, { responseType: 'blob' });
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      downloadBlob(resp.data, `timesheets_${rangeStartDate}_to_${rangeEndDate}.${ext}`);
      toast.success(`${format.toUpperCase()} generated`);
      onOpenChange(false);
    } catch (e) {
      const msg = e.response?.status === 404 ? 'No timesheets found in this range' : `Failed to generate ${format.toUpperCase()}`;
      toast.error(msg);
    } finally { setDownloading(false); }
  };

  // ---- Employee Report PDF/Excel ----
  const generateEmployeeReport = async (format) => {
    if (!empReportEmployee || !empStartDate || !empEndDate) { toast.error(t('auto.proj.selectEmployeeAndDates')); return; }
    setDownloading(true);
    try {
      const endpoint = format === 'pdf' ? 'pdf' : 'excel';
      const resp = await axios.post(`${API}/reports/employee-timesheet/${endpoint}`, {
        employee_id: empReportEmployee, start_date: empStartDate, end_date: empEndDate
      }, { responseType: 'blob' });
      const emp = employees.find(e => e.id === empReportEmployee);
      const name = (emp?.name || 'employee').replace(/\s/g, '_');
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      downloadBlob(resp.data, `timesheet_${name}_${empStartDate}_to_${empEndDate}.${ext}`);
      toast.success(`${format.toUpperCase()} generated`);
      onOpenChange(false);
    } catch (e) {
      const msg = e.response?.status === 404 ? 'No timesheets found for this employee in the selected range' : `Failed to generate ${format.toUpperCase()}`;
      toast.error(msg);
    } finally { setDownloading(false); }
  };

  // ---- Batch (multi-employee) report ----
  const generateBatchReport = async (format) => {
    if (!batchStartDate || !batchEndDate) { toast.error(t('auto.proj.pickADateRange')); return; }
    if (!batchAllEmployees && batchSelectedIds.length === 0) {
      toast.error(t('auto.proj.pickAtLeastOneEmployeeOr'));
      return;
    }
    setDownloading(true);
    try {
      const body = {
        all_employees: batchAllEmployees,
        employee_ids: batchAllEmployees ? null : batchSelectedIds,
        start_date: batchStartDate,
        end_date: batchEndDate,
        format,
      };
      const resp = await axios.post(`${API}/reports/employee-timesheet/batch`, body, { responseType: 'blob' });
      downloadBlob(resp.data, `timesheets_batch_${batchStartDate}_to_${batchEndDate}.zip`);
      const included = resp.headers['x-reports-included'];
      const skipped = resp.headers['x-reports-skipped'];
      const skippedSuffix = skipped && parseInt(skipped, 10) > 0 ? `; ${skipped} skipped (no entries)` : '';
      toast.success(`ZIP ready · ${included} reports included${skippedSuffix}. See _summary.txt for details.`);
      onOpenChange(false);
    } catch (e) {
      const msg = e.response?.status === 404
        ? 'No employees had timesheet entries in the selected range'
        : `Batch failed: ${e.response?.data?.detail || e.message}`;
      toast.error(msg);
    } finally { setDownloading(false); }
  };

  const toggleBatchEmployee = (id) => {
    setBatchSelectedIds((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]
    );
  };

  // ---- Email each employee their own report ----
  const emailBatchReport = async (format) => {
    if (!batchStartDate || !batchEndDate) { toast.error(t('auto.proj.pickADateRange')); return; }
    if (!batchAllEmployees && batchSelectedIds.length === 0) {
      toast.error(t('auto.proj.pickAtLeastOneEmployeeOr'));
      return;
    }
    const target = batchAllEmployees ? employees.length : batchSelectedIds.length;
    if (!window.confirm(
      `Send ${format.toUpperCase()} timesheet reports for ${batchStartDate} → ${batchEndDate} ` +
      `to ${target} employee(s) via email?\n\nEmployees without an email address will be skipped.`
    )) return;

    setDownloading(true);
    try {
      const body = {
        all_employees: batchAllEmployees,
        employee_ids: batchAllEmployees ? null : batchSelectedIds,
        start_date: batchStartDate,
        end_date: batchEndDate,
        format,
      };
      const resp = await axios.post(`${API}/reports/employee-timesheet/batch/email`, body);
      const { sent = [], skipped = [], failed = [], summary } = resp.data || {};
      if (sent.length && !failed.length) {
        toast.success(`${summary}`);
      } else if (sent.length) {
        toast.warning(`${summary} — open the browser console for the per-employee log.`);
        // eslint-disable-next-line no-console
        console.warn('[Batch email] sent:', sent, 'skipped:', skipped, 'failed:', failed);
      } else {
        toast.error(summary || 'Nothing sent — check SMTP settings and employee emails');
        // eslint-disable-next-line no-console
        console.warn('[Batch email] skipped:', skipped, 'failed:', failed);
      }
      if (sent.length) onOpenChange(false);
    } catch (e) {
      const msg = e.response?.status === 404
        ? 'No matching employees in the database'
        : `Batch email failed: ${e.response?.data?.detail || e.message}`;
      toast.error(msg);
    } finally { setDownloading(false); }
  };

  const getSelectionSummary = () => {
    const parts = [];
    parts.push(filterOptions.allEmployees ? 'All employees' : `${filterOptions.selectedEmployees.length}/${relevantEmployees.length} employees`);
    parts.push(filterOptions.allProjects ? 'All projects' : `${filterOptions.selectedProjects.length}/${relevantProjects.length} projects`);
    parts.push(filterOptions.allDays ? 'All days' : `${filterOptions.selectedDays.length}/7 days`);
    return parts.join(' \u2022 ');
  };

  const tabs = [
    { id: 'single', label: t('auto.lbl.singleWeek'), icon: Calendar },
    { id: 'range', label: t('auto.lbl.dateRange'), icon: CalendarRange },
    { id: 'employee', label: t('auto.lbl.employeeReport'), icon: User },
    { id: 'batch', label: t('auto.lbl.multiEmployee'), icon: UsersRound },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="timesheet-print-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Filter className="h-5 w-5" />
            <span>{t('auto.proj.timesheetReports')}</span>
          </DialogTitle>
          <DialogDescription className="sr-only">{t('auto.proj.generateTimesheetReportsInPdfOr')}</DialogDescription>
        </DialogHeader>

        {/* Tab Navigation */}
        <div className="flex overflow-x-auto border-b mb-4" data-testid="report-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id ? 'border-red-600 text-red-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* ===== Single Week Tab ===== */}
        {activeTab === 'single' && (
          <div className="space-y-4">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-3">
                <div className="text-sm font-medium text-blue-800">
                  {weekRange} &mdash; {getSelectionSummary()}
                </div>
                {getCustomerNames().length > 0 && (
                  <div className="text-sm text-blue-700 mt-1">Customer(s): {getCustomerNames().join(', ')}</div>
                )}
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Employee Filter */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1"><Users className="h-4 w-4" />Employees ({relevantEmployees.length})</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox id="all-employees" checked={filterOptions.allEmployees} onCheckedChange={handleAllEmployeesToggle} />
                    <Label htmlFor="all-employees" className="text-sm font-medium">{t('auto.proj.selectAll2')}</Label>
                  </div>
                  <Separator />
                  <div className="space-y-1.5 max-h-32 overflow-y-auto">
                    {relevantEmployees.map(emp => (
                      <div key={emp.id} className="flex items-center space-x-2">
                        <Checkbox id={`emp-${emp.id}`} checked={filterOptions.selectedEmployees.includes(emp.id)} onCheckedChange={() => handleEmployeeToggle(emp.id)} />
                        <Label htmlFor={`emp-${emp.id}`} className="text-xs">{emp.name || emp.employee_code}</Label>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Project Filter */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1"><Building2 className="h-4 w-4" />Projects ({relevantProjects.length})</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox id="all-projects" checked={filterOptions.allProjects} onCheckedChange={handleAllProjectsToggle} />
                    <Label htmlFor="all-projects" className="text-sm font-medium">{t('auto.proj.selectAll2')}</Label>
                  </div>
                  <Separator />
                  <div className="space-y-1.5 max-h-32 overflow-y-auto">
                    {relevantProjects.map(proj => (
                      <div key={proj.id} className="flex items-center space-x-2">
                        <Checkbox id={`proj-${proj.id}`} checked={filterOptions.selectedProjects.includes(proj.id)} onCheckedChange={() => handleProjectToggle(proj.id)} />
                        <Label htmlFor={`proj-${proj.id}`} className="text-xs">{proj.name}</Label>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Days Filter */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1"><Calendar className="h-4 w-4" />{t('auto.proj.days')}</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox id="all-days" checked={filterOptions.allDays} onCheckedChange={handleAllDaysToggle} />
                    <Label htmlFor="all-days" className="text-sm font-medium">{t('auto.proj.selectAll2')}</Label>
                  </div>
                  <Separator />
                  <div className="space-y-1.5">
                    {daysOfWeek.map(day => (
                      <div key={day.id} className="flex items-center space-x-2">
                        <Checkbox id={`day-${day.id}`} checked={filterOptions.selectedDays.includes(day.id)} onCheckedChange={() => handleDayToggle(day.id)} />
                        <Label htmlFor={`day-${day.id}`} className="text-xs">{day.label}</Label>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">{t('auto.proj.notesOptional2')}</CardTitle></CardHeader>
              <CardContent>
                <textarea className="w-full border rounded-md p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" rows={2}
                  placeholder={t('auto.proj.additionalNotesForTheTimesheet')} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </CardContent>
            </Card>

            <div className="flex justify-end space-x-3 pt-3 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="btn-cancel">{t('auto.proj.cancel')}</Button>
              <DocLangToggle testId="doc-lang-timesheetprintdialog-0" />
              <Button variant="outline" onClick={generateSingleExcel} disabled={downloadingExcel} className="bg-green-50 hover:bg-green-100 border-green-300" data-testid="single-excel-btn">
                <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
                {downloadingExcel ? t('auto.proj.generating') : t('auto.proj.exportExcel')}
              </Button>
              <Button onClick={generateSinglePDF} disabled={downloading} data-testid="single-pdf-btn">
                <Download className="h-4 w-4 mr-2" />
                {downloading ? t('auto.proj.generating') : t('auto.proj.generatePdf')}
              </Button>
            </div>
          </div>
        )}

        {/* ===== Date Range Tab ===== */}
        {activeTab === 'range' && (
          <div className="space-y-4">
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-3">
                <div className="text-sm text-amber-800">
                  {t('auto.proj.generateIdenticalSingleWeekReportsFor')}
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium mb-1 block">{t('auto.proj.startDate2')}</Label>
                <Input type="date" className="text-sm focus-visible:ring-2 focus-visible:ring-blue-500"
                  value={rangeStartDate} onChange={e => setRangeStartDate(e.target.value)} data-testid="range-start-date" />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1 block">{t('auto.proj.endDate')}</Label>
                <Input type="date" className="text-sm focus-visible:ring-2 focus-visible:ring-blue-500"
                  value={rangeEndDate} onChange={e => setRangeEndDate(e.target.value)} data-testid="range-end-date" />
              </div>
            </div>
            <div className="flex items-center gap-2 -mt-1">
              <span className="text-xs text-gray-500">{t('weekFilter.orPickWeek')}</span>
              <WeekRangeFilter from={rangeStartDate} to={rangeEndDate} onChange={(f, t2) => { setRangeStartDate(f); setRangeEndDate(t2); }} testId="range-week-filter" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Employee Filter */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1"><Users className="h-4 w-4" />{t('auto.proj.employees')}</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox id="range-all-employees" checked={rangeFilterOptions.allEmployees} onCheckedChange={(c) => setRangeFilterOptions(prev => ({
                      ...prev, allEmployees: c, selectedEmployees: c ? employees.map(e => e.id) : []
                    }))} />
                    <Label htmlFor="range-all-employees" className="text-sm font-medium">{t('auto.proj.selectAll2')}</Label>
                  </div>
                  <Separator />
                  <div className="space-y-1.5 max-h-32 overflow-y-auto">
                    {employees.map(emp => (
                      <div key={emp.id} className="flex items-center space-x-2">
                        <Checkbox id={`range-emp-${emp.id}`} checked={rangeFilterOptions.selectedEmployees.includes(emp.id)} onCheckedChange={() => {
                          setRangeFilterOptions(prev => {
                            const sel = prev.selectedEmployees.includes(emp.id) ? prev.selectedEmployees.filter(id => id !== emp.id) : [...prev.selectedEmployees, emp.id];
                            return { ...prev, selectedEmployees: sel, allEmployees: sel.length === employees.length };
                          });
                        }} />
                        <Label htmlFor={`range-emp-${emp.id}`} className="text-xs">{emp.name || emp.employee_code}</Label>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Project Filter */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1"><Building2 className="h-4 w-4" />{t('auto.proj.projects')}</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox id="range-all-projects" checked={rangeFilterOptions.allProjects} onCheckedChange={(c) => setRangeFilterOptions(prev => ({
                      ...prev, allProjects: c, selectedProjects: c ? projects.map(p => p.id) : []
                    }))} />
                    <Label htmlFor="range-all-projects" className="text-sm font-medium">{t('auto.proj.selectAll2')}</Label>
                  </div>
                  <Separator />
                  <div className="space-y-1.5 max-h-32 overflow-y-auto">
                    {projects.map(proj => (
                      <div key={proj.id} className="flex items-center space-x-2">
                        <Checkbox id={`range-proj-${proj.id}`} checked={rangeFilterOptions.selectedProjects.includes(proj.id)} onCheckedChange={() => {
                          setRangeFilterOptions(prev => {
                            const sel = prev.selectedProjects.includes(proj.id) ? prev.selectedProjects.filter(id => id !== proj.id) : [...prev.selectedProjects, proj.id];
                            return { ...prev, selectedProjects: sel, allProjects: sel.length === projects.length };
                          });
                        }} />
                        <Label htmlFor={`range-proj-${proj.id}`} className="text-xs">{proj.name}</Label>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Days Filter */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1"><Calendar className="h-4 w-4" />{t('auto.proj.days')}</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox id="range-all-days" checked={rangeFilterOptions.allDays} onCheckedChange={(c) => setRangeFilterOptions(prev => ({
                      ...prev, allDays: c, selectedDays: c ? daysOfWeek.map(d => d.id) : []
                    }))} />
                    <Label htmlFor="range-all-days" className="text-sm font-medium">{t('auto.proj.selectAll2')}</Label>
                  </div>
                  <Separator />
                  <div className="space-y-1.5">
                    {daysOfWeek.map(day => (
                      <div key={day.id} className="flex items-center space-x-2">
                        <Checkbox id={`range-day-${day.id}`} checked={rangeFilterOptions.selectedDays.includes(day.id)} onCheckedChange={() => {
                          setRangeFilterOptions(prev => {
                            const sel = prev.selectedDays.includes(day.id) ? prev.selectedDays.filter(id => id !== day.id) : [...prev.selectedDays, day.id];
                            return { ...prev, selectedDays: sel, allDays: sel.length === daysOfWeek.length };
                          });
                        }} />
                        <Label htmlFor={`range-day-${day.id}`} className="text-xs">{day.label}</Label>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="flex justify-end space-x-3 pt-3 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="btn-cancel">{t('auto.proj.cancel')}</Button>
              <DocLangToggle testId="doc-lang-timesheetprintdialog-1" />
              <Button variant="outline" onClick={() => generateRangeReport('excel')} disabled={downloading} className="bg-green-50 hover:bg-green-100 border-green-300" data-testid="range-excel-btn">
                <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
                {downloading ? t('auto.proj.generating') : t('auto.proj.exportExcel')}
              </Button>
              <Button onClick={() => generateRangeReport('pdf')} disabled={downloading} data-testid="range-pdf-btn">
                <Download className="h-4 w-4 mr-2" />
                {downloading ? t('auto.proj.generating') : t('auto.proj.generatePdf')}
              </Button>
            </div>
          </div>
        )}

        {/* ===== Employee Report Tab ===== */}
        {activeTab === 'employee' && (
          <div className="space-y-4">
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="p-3">
                <div className="text-sm text-purple-800">
                  {t('auto.proj.generateATimesheetReportForA')}
                </div>
              </CardContent>
            </Card>

            <div>
              <Label className="text-sm font-medium mb-1 block">{t('auto.proj.employee')}</Label>
              <Select value={empReportEmployee} onValueChange={setEmpReportEmployee}>
                <SelectTrigger data-testid="employee-report-select">
                  <SelectValue placeholder={t('auto.proj.selectEmployee3')} />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name || emp.employee_code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium mb-1 block">{t('auto.proj.startDate2')}</Label>
                <Input type="date" className="text-sm focus-visible:ring-2 focus-visible:ring-blue-500"
                  value={empStartDate} onChange={e => setEmpStartDate(e.target.value)} data-testid="emp-start-date" />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1 block">{t('auto.proj.endDate')}</Label>
                <Input type="date" className="text-sm focus-visible:ring-2 focus-visible:ring-blue-500"
                  value={empEndDate} onChange={e => setEmpEndDate(e.target.value)} data-testid="emp-end-date" />
              </div>
            </div>
            <div className="flex items-center gap-2 -mt-1">
              <span className="text-xs text-gray-500">{t('weekFilter.orPickWeek')}</span>
              <WeekRangeFilter from={empStartDate} to={empEndDate} onChange={(f, t2) => { setEmpStartDate(f); setEmpEndDate(t2); }} testId="emp-week-filter" />
            </div>

            <div className="flex justify-end space-x-3 pt-3 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="btn-cancel">{t('auto.proj.cancel')}</Button>
              <DocLangToggle testId="doc-lang-timesheetprintdialog-2" />
              <Button variant="outline" onClick={() => generateEmployeeReport('excel')} disabled={downloading} className="bg-green-50 hover:bg-green-100 border-green-300" data-testid="emp-excel-btn">
                <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
                {downloading ? t('auto.proj.generating') : t('auto.proj.exportExcel')}
              </Button>
              <Button onClick={() => generateEmployeeReport('pdf')} disabled={downloading} data-testid="emp-pdf-btn">
                <Download className="h-4 w-4 mr-2" />
                {downloading ? t('auto.proj.generating') : t('auto.proj.generatePdf')}
              </Button>
            </div>
          </div>
        )}

        {/* ===== Multi-Employee (Batch) Tab ===== */}
        {activeTab === 'batch' && (
          <div className="space-y-4" data-testid="batch-panel">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-1">{t('auto.proj.multiEmployeeReports')}</h3>
              <p className="text-sm text-blue-700">
                Generate one timesheet report per selected employee for the same date window and bundle them into a single ZIP download.
                Employees with no entries in the range are skipped silently — a <code>_summary.txt</code> inside the ZIP lists what was included and skipped.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>{t('auto.proj.startDate2')}</Label>
                <Input type="date" value={batchStartDate} onChange={(e) => setBatchStartDate(e.target.value)} data-testid="batch-start" />
              </div>
              <div>
                <Label>{t('auto.proj.endDate')}</Label>
                <Input type="date" value={batchEndDate} onChange={(e) => setBatchEndDate(e.target.value)} data-testid="batch-end" />
              </div>
            </div>
            <div className="flex items-center gap-2 -mt-1">
              <span className="text-xs text-gray-500">{t('weekFilter.orPickWeek')}</span>
              <WeekRangeFilter from={batchStartDate} to={batchEndDate} onChange={(f, t2) => { setBatchStartDate(f); setBatchEndDate(t2); }} testId="batch-week-filter" />
            </div>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1">
                    <Users className="h-4 w-4" /> {t('auto.proj.employees')}
                  </span>
                  <label className="inline-flex items-center gap-2 text-xs font-normal">
                    <Checkbox
                      checked={batchAllEmployees}
                      onCheckedChange={(v) => setBatchAllEmployees(!!v)}
                      data-testid="batch-all"
                    />
                    {t('auto.proj.allActiveEmployees')}
                  </label>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {batchAllEmployees ? (
                  <div className="text-xs text-slate-500 italic">
                    All {employees.length} active employees will be processed. Toggle off to pick a subset.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-1 max-h-56 overflow-y-auto">
                    {employees.map((e) => (
                      <label key={e.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
                        <Checkbox
                          checked={batchSelectedIds.includes(e.id)}
                          onCheckedChange={() => toggleBatchEmployee(e.id)}
                          data-testid={`batch-emp-${e.id}`}
                        />
                        <span className="truncate" title={e.name}>{e.name}</span>
                      </label>
                    ))}
                  </div>
                )}
                {!batchAllEmployees && (
                  <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500">
                    <button
                      type="button"
                      className="hover:text-slate-800 underline"
                      onClick={() => setBatchSelectedIds(employees.map((e) => e.id))}
                    >
                      {t('auto.proj.selectAll')}
                    </button>
                    <button
                      type="button"
                      className="hover:text-slate-800 underline"
                      onClick={() => setBatchSelectedIds([])}
                    >
                      {t('auto.proj.clear')}
                    </button>
                    <span>· {batchSelectedIds.length}/{employees.length} selected</span>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex justify-end space-x-3 pt-3 border-t flex-wrap gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="btn-cancel">{t('auto.proj.cancel')}</Button>
              <Button
                variant="outline"
                onClick={() => emailBatchReport('pdf')}
                disabled={downloading}
                className="bg-blue-50 hover:bg-blue-100 border-blue-300"
                data-testid="batch-email-btn"
                title={t('auto.proj.sendEachEmployeeTheirPdfVia')}
              >
                <Mail className="h-4 w-4 mr-2 text-blue-600" />
                {downloading ? t('auto.proj.sending') : t('auto.proj.emailToEachEmployee')}
              </Button>
              <DocLangToggle testId="doc-lang-timesheetprintdialog-3" />
              <Button
                variant="outline"
                onClick={() => generateBatchReport('excel')}
                disabled={downloading}
                className="bg-green-50 hover:bg-green-100 border-green-300"
                data-testid="batch-excel-btn"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
                {downloading ? t('auto.proj.generating') : t('auto.proj.exportExcelZip')}
              </Button>
              <Button
                onClick={() => generateBatchReport('pdf')}
                disabled={downloading}
                data-testid="batch-pdf-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                {downloading ? t('auto.proj.generating') : t('auto.proj.generatePdfZip')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TimesheetPrintDialog;
